import Dispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes, StatTypes, SearchTypes } from '../constants/CentricConstants';

import CourseApi from '../server/CourseApi';

function createSuccessHandler() {
    let actions = Array.from(arguments);
    return function(response) {
        actions.forEach(function(action) {
            Dispatcher.dispatch({
                type: action,
                data: response.data
            });
        });
    }
}

function createErrorHandler() {
    let actions = Array.from(arguments);
    return function(response) {
        actions.forEach(function(action) {
            Dispatcher.dispatch({
                type: action,
                data: response.message
            });
        });
    }
}

const _handleFormError = createErrorHandler(ActionTypes.FORM_SUBMIT_ERROR);
const _handleException = createErrorHandler(ActionTypes.API_EXCEPTION);

export default {

    getAll: (params, clearAll = true) => {
        const types = {
            success: ActionTypes.COURSE_LIST,
            failure: ActionTypes.COURSE_LIST_ERROR,
        };

        return Dispatcher.dispatchAsync(CourseApi.getAll(params), types, {params, clearAll});
    },
    getMy: function(params) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_LIST);
        const handleError = createErrorHandler(ActionTypes.COURSE_LIST_ERROR);

        return CourseApi.getMy(params)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getById: function(id) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_INFO);
        const handleError = createErrorHandler(ActionTypes.COURSE_INFO_ERROR);

        return CourseApi.getById(id)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getByCategory: (categoryId, params) => {
        const types = {
            success:    ActionTypes.COURSE_LIST,
            failure:    ActionTypes.COURSE_LIST_ERROR
        };

        return Dispatcher.dispatchAsync(CourseApi.getByCategory(categoryId, params), types, {categoryId, params, clearAll: true});
    },
    getBySkill: (skillId) => {
        const types = {
            success:    ActionTypes.COURSE_LIST,
            failure:    ActionTypes.COURSE_LIST_ERROR
        };

        return Dispatcher.dispatchAsync(CourseApi.getBySkill(skillId), types, {skillId});
    },
    getSimplifiedList: (courseIds) => {
        const types = {
            success:    ActionTypes.COURSE_LIST,
            failure:    ActionTypes.COURSE_LIST_ERROR
        };

        return Dispatcher.dispatchAsync(CourseApi.getSimplifiedList(courseIds), types, {courseIds});
    },

    save: function(course) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_SAVE, ActionTypes.FORM_SUBMIT_SUCCESS);
        const handleError = createErrorHandler(ActionTypes.COURSE_INFO_ERROR, ActionTypes.FORM_SUBMIT_ERROR);

        return CourseApi.save(course)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    update: function(id, course) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_UPDATE, ActionTypes.FORM_SUBMIT_SUCCESS);
        const handleError = createErrorHandler(ActionTypes.COURSE_UPDATE_ERROR);

        return CourseApi.update(id, course)
            .then(handleSuccess, _handleFormError)
            .catch(_handleException);
    },
    delete: function(courseId) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_DELETE);
        const handleError = createErrorHandler(ActionTypes.COURSE_DELETE_ERROR);

        return CourseApi.delete(courseId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getStatusOptions: function() {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_STATUS_OPTION_LIST);
        const handleError = createErrorHandler(ActionTypes.COURSE_INFO_ERROR);

        return CourseApi.getStatusOptions()
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getSimplifiedStatusList: () => {
        const types = {
            success: ActionTypes.COURSE_STATUS_OPTION_LIST,
            failure: ActionTypes.COURSE_INFO_ERROR
        };

        return Dispatcher.dispatchAsync(CourseApi.getSimplifiedStatusList(), types);
    },
    getTypeOptions: function() {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_TYPE_OPTION_LIST);
        const handleError = createErrorHandler(ActionTypes.COURSE_INFO_ERROR);

        return CourseApi.getTypeOptions()
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getAssets: function(courseId) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_ASSET_LIST);
        const handleError = createErrorHandler(ActionTypes.COURSE_ASSET_LIST_ERROR);

        return CourseApi.getAssets(courseId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    addAsset: function(courseId, assetId) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_ASSET_ADD);
        const handleError = createErrorHandler(ActionTypes.COURSE_ASSET_ADD_ERROR);

        return CourseApi.addAsset(courseId, assetId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    removeAsset: function(courseId, assetId) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_ASSET_DELETE);
        const handleError = createErrorHandler(ActionTypes.COURSE_ASSET_DELETE_ERROR);

        return CourseApi.removeAsset(courseId, assetId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getEnrolments: (courseId, params, clearAll = false) => {
        const types = {
            success: ActionTypes.COURSE_ENROLMENT_LIST,
            failure: ActionTypes.COURSE_ENROLMENT_LIST_ERROR
        };
        return Dispatcher.dispatchAsync(CourseApi.getEnrolments(courseId, params), types, { clearAll });
    },
    searchEnrolments: (courseId, searchText, params, key = SearchTypes.COURSE_ENROLMENTS) => {
        const types = {
            request: ActionTypes.SEARCH_REQUEST,
            success: [ActionTypes.SEARCH_SUCCESS, ActionTypes.COURSE_ENROLMENT_LIST],
            failure: ActionTypes.SEARCH_FAILURE
        };
        return Dispatcher.dispatchAsync(CourseApi.getEnrolments(courseId, {searchText, ...params}), types, {courseId, searchText, params, key});
    },
    getTrainers: (courseId) => {
        const types = {
            success:    ActionTypes.COURSE_TRAINER_LIST_SUCCESS,
            failure:    ActionTypes.COURSE_TRAINER_LIST_FAILURE
        };
        return Dispatcher.dispatchAsync(CourseApi.getTrainers(courseId), types, {courseId});
    },
    getAllEnrolments: () => {
        const types = {
            success: ActionTypes.COURSE_ENROLMENT_LIST,
            failure: ActionTypes.COURSE_ENROLMENT_LIST_ERROR
        };
        return Dispatcher.dispatchAsync(CourseApi.getAllEnrolments(), types);
    },
    getEnrolment: (id) => {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_INFO_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_INFO_SUCCESS,
            failure:    ActionTypes.COURSE_ENROLMENT_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.getEnrolment(id), types, {id});
    },
    getEnrolmentsByUserId: (id, params) => {
        const types = {
            success: ActionTypes.COURSE_ENROLMENT_LIST,
            failure: ActionTypes.COURSE_ENROLMENT_LIST_ERROR
        };
        return Dispatcher.dispatchAsync(CourseApi.getEnrolmentsByUserId(id, params), types, {id, params});
    },

    getEnrolmentProgressByUser: (userId) => {
        const types = {
            request:    ActionTypes.COURSE_PROGRESS_LIST_REQUEST,
            success:    ActionTypes.COURSE_PROGRESS_LIST_SUCCESS,
            failure:    ActionTypes.COURSE_PROGRESS_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.getEnrolmentProgressByUser(userId), types, { userId });
    },

    getMyEnrolment: (courseId) => {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_INFO_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_INFO_SUCCESS,
            failure:    ActionTypes.COURSE_ENROLMENT_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.getMyEnrolment(courseId), types, {courseId});
    },

    getComplianceByUserId: (userId) => {
        const types = {
            request:    ActionTypes.COURSE_COMPLIANCE_LIST_REQUEST,
            success:    ActionTypes.COURSE_COMPLIANCE_LIST_SUCCESS,
            failure:    ActionTypes.COURSE_COMPLIANCE_LIST_FAILURE
        };
        return Dispatcher.dispatchAsync(CourseApi.getCompliance(userId), types, {userId});
    },

    enrollUser: function(id, params) {
        const handleSuccess = createSuccessHandler(ActionTypes.USER_COURSE_ENROL);
        const handleError   = createErrorHandler(ActionTypes.USER_COURSE_ENROL_ERROR);

        return CourseApi.enrollUser(id, params)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    enrolByFilter: (moduleId, filterId, params) => {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_BY_FILTER_SAVE_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_BY_FILTER_SAVE_SUCCESS,
            failure:    [ActionTypes.COURSE_ENROLMENT_BY_FILTER_SAVE_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };
        return Dispatcher.dispatchAsync(CourseApi.enrolByFilter(moduleId, filterId, params), types, {moduleId, filterId, params});
    },

    updateEnrolment: function(courseId, enrolmentId, params) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_ENROLMENT_UPDATE_SUCCESS, ActionTypes.FORM_SUBMIT_SUCCESS);

        return CourseApi.updateEnrolment(courseId, enrolmentId, params)
            .then(handleSuccess, _handleFormError)
            .catch(_handleException);
    },

    resetEnrolment: (courseId, enrolmentId, params) => {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_UPDATE_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_UPDATE_SUCCESS,
            failure:    ActionTypes.COURSE_ENROLMENT_UPDATE_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.resetEnrolment(courseId, enrolmentId, params), types, {courseId, enrolmentId, params});
    },

    bulkUpdateEnrolments: (enrolmentIds, params) => {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_BULK_UPDATE_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_BULK_UPDATE_SUCCESS,
            failure:    [ActionTypes.COURSE_ENROLMENT_BULK_UPDATE_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };
        return Dispatcher.dispatchAsync(CourseApi.bulkUpdateEnrolments({enrolmentIds, ...params}), types, {enrolmentIds, params});
    },

    checkCompletion: (enrolmentId) => {
        const types = {
            request: ActionTypes.COURSE_ENROLMENT_CHECK_COMPLETE_REQUEST,
            success: ActionTypes.COURSE_ENROLMENT_CHECK_COMPLETE_SUCCESS,
            failure: ActionTypes.COURSE_ENROLMENT_CHECK_COMPLETE_FAILURE
        };
        return Dispatcher.dispatchAsync(CourseApi.checkCompletion(enrolmentId), types, {enrolmentId});
    },

    deleteEnrolment: (enrolment) => {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_DELETE_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_DELETE_SUCCESS,
            failure:    ActionTypes.COURSE_ENROLMENT_DELETE_FAILURE
        };

        Dispatcher.dispatchAsync(CourseApi.deleteEnrolment(enrolment.courseId, enrolment.id), types, {enrolment});
    },

    bulkDeleteEnrolments: (enrolmentIds) => {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_BULK_DELETE_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_BULK_DELETE_SUCCESS,
            failure:    [ActionTypes.COURSE_ENROLMENT_BULK_DELETE_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };
        return Dispatcher.dispatchAsync(CourseApi.bulkDeleteEnrolments({enrolmentIds}), types, {enrolmentIds});
    },

    sendEnrolmentEmails: (enrolmentIds) => {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_EMAIL_SEND_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_EMAIL_SEND_SUCCESS,
            failure:    ActionTypes.COURSE_ENROLMENT_EMAIL_SEND_FAILURE
        };
        return Dispatcher.dispatchAsync(CourseApi.sendEnrolmentEmails({enrolmentIds}), types, {enrolmentIds});
    },

    search: (searchText, params, key = SearchTypes.COURSES) => {
        const types = {
            request:    ActionTypes.SEARCH_REQUEST,
            success:    [ActionTypes.SEARCH_SUCCESS, ActionTypes.COURSE_LIST], //TODO: Remove COURSE_LIST when infinite scroll is reworked
            failure:    ActionTypes.SEARCH_FAILURE
        };

        Dispatcher.dispatchAsync(CourseApi.search(searchText, params), types, {searchText, params, key});
    },
    searchMy: function(searchText, params) {
        const types = {
            request: ActionTypes.SEARCH_REQUEST,
            success: [ActionTypes.SEARCH_SUCCESS, ActionTypes.COURSE_LIST],
            failure: ActionTypes.SEARCH_FAILURE
        };

        Dispatcher.dispatchAsync(CourseApi.searchMy(searchText, params), types, {searchText, params});
    },
    getMediaInfo: function(id) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_MEDIA_INFO);
        const handleError   = createErrorHandler(ActionTypes.COURSE_MEDIA_INFO_ERROR);

        return CourseApi.getMediaInfo(id)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getCount: function() {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.getCount(), types, { key: StatTypes.COURSE_COUNT } );
    },
    getStats: function() {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.getStats(), types, { key: StatTypes.COURSE_STATS } );
    },

    getMyProgress: function() {
        const types = {
            request:    ActionTypes.COURSE_PROGRESS_LIST_REQUEST,
            success:    ActionTypes.COURSE_PROGRESS_LIST_SUCCESS,
            failure:    ActionTypes.COURSE_PROGRESS_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.getMyProgress(), types);
    },

    getMyEnrolmentStats: function() {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.getMyEnrolmentStats(), types, { key: StatTypes.MY_ENROLMENTS } );
    },

    getStatsGrouped: function(groupBy, sort) {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_STATS_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_STATS_SUCCESS,
            failure:    ActionTypes.COURSE_ENROLMENT_STATS_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.getStatsGrouped(groupBy), types, { groupBy, sort });
    },

    getCourseEnrolmentStats: (courseId) => {
        const types = {
            request:    ActionTypes.COURSE_ENROLMENT_STATS_REQUEST,
            success:    ActionTypes.COURSE_ENROLMENT_STATS_SUCCESS,
            failure:    ActionTypes.COURSE_ENROLMENT_STATS_FAILURE
        };
        return Dispatcher.dispatchAsync(CourseApi.getCourseEnrolmentStats(courseId), types, {groupBy: 'courseId', courseId, suppressErrors: true});
    },

    getModule: (id, moduleId) => {
        const types = {
            request:    ActionTypes.COURSE_MODULE_INFO_REQUEST,
            success:    ActionTypes.COURSE_MODULE_INFO_SUCCESS,
            failure:    ActionTypes.COURSE_MODULE_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.getModule(id, moduleId), types, { id, moduleId })
    },
    getModules: function(id) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_MODULE_LIST);
        const handleError = createErrorHandler(ActionTypes.COURSE_MODULE_LIST_ERROR);

        return CourseApi.getModules(id)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    addModule: function(id, moduleId) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_MODULE_SAVE);
        const handleError = createErrorHandler(ActionTypes.COURSE_MODULE_SAVE_ERROR, ActionTypes.FORM_SUBMIT_ERROR);

        return CourseApi.addModule(id, moduleId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    updateModules: function(id, data) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_MODULE_LIST, ActionTypes.FORM_SUBMIT_SUCCESS);
        const handleError = createErrorHandler(ActionTypes.COURSE_MODULE_UPDATE_ERROR, ActionTypes.FORM_SUBMIT_ERROR);

        return CourseApi.updateModules(id, data)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    designateModule: function(id, moduleId) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_UPDATE);
        const handleError = createErrorHandler(ActionTypes.COURSE_UPDATE_ERROR, ActionTypes.FORM_SUBMIT_ERROR);

        return CourseApi.designateModule(id, moduleId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    releaseModule: function(id, moduleId) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_UPDATE);
        const handleError = createErrorHandler(ActionTypes.COURSE_UPDATE_ERROR, ActionTypes.FORM_SUBMIT_ERROR);

        return CourseApi.releaseModule(id)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    deleteModule: function(id, moduleId) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_MODULE_DELETE);
        const handleError = createErrorHandler(ActionTypes.COURSE_MODULE_DELETE_ERROR, ActionTypes.FORM_SUBMIT_ERROR);

        return CourseApi.deleteModule(id, moduleId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    moveModuleUp: (id, moduleId) => {
        const types = {
            request: ActionTypes.COURSE_MODULE_MOVE_UP_REQUEST,
            success: ActionTypes.COURSE_MODULE_MOVE_UP_SUCCESS,
            failure: ActionTypes.COURSE_MODULE_MOVE_UP_FAILURE
        };
        return Dispatcher.dispatchAsync(CourseApi.moveModuleUp(id, moduleId), types, {id, moduleId});
    },

    getTinCanList: function(id, userId) {
        const handleSuccess = createSuccessHandler(ActionTypes.COURSE_ACTIVITY_TINCAN_LIST);
        const handleError = createErrorHandler(ActionTypes.COURSE_ACTIVITY_TINCAN_LIST_ERROR);

        return CourseApi.getTinCanList(id, userId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },

    addLabel: (id, label) => {
        const types = {
            request: ActionTypes.COURSE_LABEL_ADD_REQUEST,
            success: ActionTypes.COURSE_LABEL_ADD_SUCCESS,
            failure: ActionTypes.COURSE_LABEL_ADD_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.addLabel(id, label.id), types, {id, label});
    },

    removeLabel: (id, labelId) => {
        const types = {
            request: ActionTypes.COURSE_LABEL_REMOVE_REQUEST,
            success: ActionTypes.COURSE_LABEL_REMOVE_SUCCESS,
            failure: ActionTypes.COURSE_LABEL_REMOVE_FAILURE
        };

        return Dispatcher.dispatchAsync(CourseApi.removeLabel(id, labelId), types, {id, labelId});
    }
};
